"use client";

/**
 * React Query bindings for the files browser.
 *
 * One query (`useFilesListing`) feeds every component that needs to
 * see the user's folders + files. Every mutation hook optimistically
 * patches the same cache entry, so the UI updates in the same render
 * the user clicks — no per-mount React state, no window-event
 * broadcasts, no module-level cache.
 *
 * Failure handling pattern (used by every mutation):
 *   onMutate: snapshot the previous cache + apply optimistic patch
 *   onError: restore the snapshot + toast a human message
 *   onSettled: invalidate so a final server-truth fetch reconciles
 *
 * The Socket.io USER_FILE_SYNCED event also invalidates the query,
 * so server-side changes (agent writes, parallel device, etc.) flow
 * back in without a manual refresh.
 */

import { useEffect, useMemo } from "react";
import {
  useMutation,
  useQuery,
  useQueryClient,
  type QueryClient,
} from "@tanstack/react-query";
import type { UserFile, UserFolder, UserFilesListing } from "@bash/shared";
import { ANALYTICS_EVENTS } from "@bash/shared";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/lib/supabase/client";
import { useConnectionStore } from "@/stores/connectionStore";
import { EVENTS } from "@/constants/events";
import { resilientFetch } from "@/lib/fetch";
import { posthog } from "@/lib/posthog";
import { useToast } from "@/components/ui/Toast";
import { uploadBatch } from "./upload-pipeline";

const SERVER_URL =
  process.env.NEXT_PUBLIC_SERVER_URL || "http://localhost:3005";

// ─── Query key + auth helpers ───────────────────────────────────────────────

/** All cache keys share this prefix so we can invalidate the whole
 *  surface in one call. Including the userId in the key gives us
 *  automatic isolation when an account-switch happens — the new user
 *  doesn't read the old user's cache. */
function filesKey(userId: string | null): readonly unknown[] {
  return ["files-listing", userId ?? "anon"] as const;
}

function useAuthHeaders() {
  const { getAccessToken } = useAuth();
  return async (jsonBody = false): Promise<Record<string, string>> => {
    const token = await getAccessToken();
    const headers: Record<string, string> = {};
    if (token) headers.Authorization = `Bearer ${token}`;
    if (jsonBody) headers["Content-Type"] = "application/json";
    return headers;
  };
}

// ─── The one query ──────────────────────────────────────────────────────────

interface ListingCache {
  folders: UserFolder[];
  files: UserFile[];
}

/** Pull the full listing. Components that render slices (root files,
 *  files-in-folder, folder tree) derive from this single source via
 *  useMemo. */
export function useFilesListing(): {
  folders: UserFolder[];
  files: UserFile[];
  loading: boolean;
  error: unknown;
} {
  const { user } = useAuth();
  const userId = user?.id ?? null;
  const getHeaders = useAuthHeaders();
  const qc = useQueryClient();

  const query = useQuery<ListingCache>({
    queryKey: filesKey(userId),
    // The signal is load-bearing, not hygiene: every mutation opens with
    // `cancelQueries` so a listing fetch that started BEFORE the write can't
    // land after it. Dropping the signal made that cancel a no-op, and a GET
    // issued before a folder existed would resolve afterwards and overwrite
    // the freshly created row — the folder then stayed invisible until a
    // reload (founder 08-06; reproduced 4/4 by creating while the listing was
    // still in flight, which is exactly what happens when you open Knowledge
    // and immediately hit New folder).
    queryFn: async ({ signal }) => {
      const headers = await getHeaders();
      const res = await resilientFetch(`${SERVER_URL}/api/files`, {
        headers,
        signal,
      });
      if (!res.ok) throw new Error("Failed to load files");
      const data: UserFilesListing = await res.json();
      return { folders: data.folders, files: data.files };
    },
    enabled: !!userId,
  });

  // Socket-driven invalidation. The server emits USER_FILE_SYNCED any
  // time it mutates a file/folder on behalf of this user (agent
  // writes, sandbox sync). Invalidate the cache so the next render
  // picks the fresh data up.
  const getSocket = useConnectionStore((s) => s.getSocket);
  const isConnected = useConnectionStore((s) => s.isSocketConnected);
  useEffect(() => {
    const socket = getSocket();
    if (!socket) return;
    const onSync = () => qc.invalidateQueries({ queryKey: filesKey(userId) });
    socket.on(EVENTS.USER_FILE_SYNCED, onSync);
    return () => {
      socket.off(EVENTS.USER_FILE_SYNCED, onSync);
    };
  }, [getSocket, isConnected, qc, userId]);

  const data = query.data;
  const folders = useMemo<UserFolder[]>(
    () => data?.folders ?? [],
    [data?.folders]
  );
  const files = useMemo<UserFile[]>(
    // Filter out reference stubs (.ref.yaml) — the listing endpoint
    // already does this, but the optimistic path inserts raw rows so
    // we double-defend here.
    () =>
      (data?.files ?? []).filter(
        (f) => !f.filename.endsWith(".ref.yaml") && !f.filename.endsWith(".ref")
      ),
    [data?.files]
  );

  return {
    folders,
    files,
    loading: query.isPending,
    error: query.error,
  };
}

// ─── Mutation helper ────────────────────────────────────────────────────────

/** Shape of the optimistic context passed from `onMutate` to `onError`. */
interface OptimisticContext {
  previous: ListingCache | undefined;
}

/** Standard onMutate/onError pattern for every mutation. Caller
 *  provides a `patch` function that returns the next cache state. */
function buildOptimisticHandlers<TVars>(
  qc: QueryClient,
  userId: string | null,
  patch: (current: ListingCache, vars: TVars) => ListingCache
) {
  return {
    onMutate: async (vars: TVars): Promise<OptimisticContext> => {
      const key = filesKey(userId);
      await qc.cancelQueries({ queryKey: key });
      const previous = qc.getQueryData<ListingCache>(key);
      if (previous) qc.setQueryData<ListingCache>(key, patch(previous, vars));
      return { previous };
    },
    onError: (
      _err: unknown,
      _vars: TVars,
      ctx: OptimisticContext | undefined
    ) => {
      if (ctx?.previous) qc.setQueryData(filesKey(userId), ctx.previous);
    },
    onSettled: () => qc.invalidateQueries({ queryKey: filesKey(userId) }),
  };
}

// ─── File mutations ─────────────────────────────────────────────────────────

export function useDeleteFile() {
  const { user } = useAuth();
  const userId = user?.id ?? null;
  const getHeaders = useAuthHeaders();
  const qc = useQueryClient();
  const { toast } = useToast();

  return useMutation<void, Error, string, OptimisticContext>({
    mutationFn: async (fileId) => {
      const headers = await getHeaders();
      const res = await resilientFetch(`${SERVER_URL}/api/files/${fileId}`, {
        method: "DELETE",
        headers,
      });
      if (!res.ok) throw new Error("Failed to delete file");
    },
    ...buildOptimisticHandlers<string>(qc, userId, (cur, fileId) => ({
      ...cur,
      files: cur.files.filter((f) => f.id !== fileId),
    })),
    onError: (err, fileId, ctx) => {
      if (ctx?.previous) qc.setQueryData(filesKey(userId), ctx.previous);
      const name =
        ctx?.previous?.files.find((f) => f.id === fileId)?.filename ?? "file";
      toast(`Couldn't delete ${name}. Try again.`);
    },
  });
}

export function useDeleteFiles() {
  const { user } = useAuth();
  const userId = user?.id ?? null;
  const getHeaders = useAuthHeaders();
  const qc = useQueryClient();
  const { toast } = useToast();

  return useMutation<void, Error, string[], OptimisticContext>({
    mutationFn: async (fileIds) => {
      const headers = await getHeaders(true);
      const res = await resilientFetch(`${SERVER_URL}/api/files/batch-delete`, {
        method: "POST",
        headers,
        body: JSON.stringify({ fileIds }),
      });
      if (!res.ok) throw new Error("Failed to delete files");
    },
    ...buildOptimisticHandlers<string[]>(qc, userId, (cur, fileIds) => {
      const set = new Set(fileIds);
      return { ...cur, files: cur.files.filter((f) => !set.has(f.id)) };
    }),
    onError: (err, fileIds, ctx) => {
      if (ctx?.previous) qc.setQueryData(filesKey(userId), ctx.previous);
      toast(
        `Couldn't delete ${fileIds.length} file${
          fileIds.length === 1 ? "" : "s"
        }. Try again.`
      );
    },
  });
}

export function useRenameFile() {
  const { user } = useAuth();
  const userId = user?.id ?? null;
  const getHeaders = useAuthHeaders();
  const qc = useQueryClient();
  const { toast } = useToast();

  return useMutation<
    void,
    Error,
    { fileId: string; newName: string },
    OptimisticContext
  >({
    mutationFn: async ({ fileId, newName }) => {
      const headers = await getHeaders(true);
      const res = await resilientFetch(`${SERVER_URL}/api/files/${fileId}`, {
        method: "PATCH",
        headers,
        body: JSON.stringify({ filename: newName }),
      });
      if (!res.ok) throw new Error("Failed to rename file");
    },
    ...buildOptimisticHandlers<{ fileId: string; newName: string }>(
      qc,
      userId,
      (cur, { fileId, newName }) => ({
        ...cur,
        files: cur.files.map((f) =>
          f.id === fileId ? { ...f, filename: newName } : f
        ),
      })
    ),
    onError: (err, { newName }, ctx) => {
      if (ctx?.previous) qc.setQueryData(filesKey(userId), ctx.previous);
      toast(`Couldn't rename to "${newName}". Reverted.`);
    },
  });
}

export function useMoveFiles() {
  const { user } = useAuth();
  const userId = user?.id ?? null;
  const getHeaders = useAuthHeaders();
  const qc = useQueryClient();
  const { toast } = useToast();

  return useMutation<
    void,
    Error,
    { fileIds: string[]; folderId: string | null },
    OptimisticContext
  >({
    mutationFn: async ({ fileIds, folderId }) => {
      const headers = await getHeaders(true);
      const res = await resilientFetch(`${SERVER_URL}/api/files/batch-move`, {
        method: "POST",
        headers,
        body: JSON.stringify({ fileIds, folderId }),
      });
      if (!res.ok) throw new Error("Failed to move files");
    },
    ...buildOptimisticHandlers<{ fileIds: string[]; folderId: string | null }>(
      qc,
      userId,
      (cur, { fileIds, folderId }) => {
        const set = new Set(fileIds);
        return {
          ...cur,
          files: cur.files.map((f) => (set.has(f.id) ? { ...f, folderId } : f)),
        };
      }
    ),
    onError: (err, { fileIds }, ctx) => {
      if (ctx?.previous) qc.setQueryData(filesKey(userId), ctx.previous);
      toast(
        `Couldn't move ${fileIds.length} file${
          fileIds.length === 1 ? "" : "s"
        }. Reverted.`
      );
    },
  });
}

// ─── Folder mutations ───────────────────────────────────────────────────────

export function useCreateFolder() {
  const { user } = useAuth();
  const userId = user?.id ?? null;
  const getHeaders = useAuthHeaders();
  const qc = useQueryClient();
  const { toast } = useToast();

  return useMutation<
    { folder: UserFolder },
    Error,
    { name: string; parentId: string | null },
    OptimisticContext & { tempId: string }
  >({
    mutationFn: async ({ name, parentId }) => {
      const headers = await getHeaders(true);
      const res = await resilientFetch(`${SERVER_URL}/api/folders`, {
        method: "POST",
        headers,
        body: JSON.stringify({ name, parentId }),
      });
      if (!res.ok) throw new Error("Failed to create folder");
      return res.json();
    },
    onMutate: async (vars) => {
      const key = filesKey(userId);
      await qc.cancelQueries({ queryKey: key });
      const previous = qc.getQueryData<ListingCache>(key);
      // Insert an optimistic folder with a temp id; the server will
      // return the real id. Renaming the temp → real happens in
      // onSuccess; in the unlikely case the user clicks the temp
      // folder before that, it'll just refetch then.
      const tempId = `__optimistic_${crypto.randomUUID()}`;
      const optimisticFolder: UserFolder = {
        id: tempId,
        parentId: vars.parentId,
        name: vars.name,
        position: Date.now(),
      };
      if (previous) {
        qc.setQueryData<ListingCache>(key, {
          ...previous,
          folders: [...previous.folders, optimisticFolder],
        });
      }
      return { previous, tempId };
    },
    onSuccess: (result, _vars, ctx) => {
      posthog.capture(ANALYTICS_EVENTS.FILES_FOLDER_CREATED, {});
      const key = filesKey(userId);
      const cur = qc.getQueryData<ListingCache>(key);
      // The optimistic patch is a HINT, never the gate: the created folder
      // must reach the grid even when onMutate couldn't seed a temp row
      // (no cache entry yet, or it was dropped between mutate and settle).
      // Both silent no-ops here used to leave a folder that EXISTS on the
      // server invisible until a reload — founder 08-06, "New folder does
      // nothing" (the POST had returned 200 the whole time).
      if (!cur) {
        void qc.invalidateQueries({ queryKey: key });
        return;
      }
      const hasTemp = cur.folders.some((f) => f.id === ctx?.tempId);
      qc.setQueryData<ListingCache>(key, {
        ...cur,
        folders: hasTemp
          ? cur.folders.map((f) => (f.id === ctx?.tempId ? result.folder : f))
          : [...cur.folders, result.folder],
      });
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.previous) qc.setQueryData(filesKey(userId), ctx.previous);
      toast(`Couldn't create folder. Try again.`);
    },
    // Converge on server truth after every create, like every other mutation
    // here (buildOptimisticHandlers ends the same way). Create was the lone
    // exception — "onSuccess already swapped the temp row" assumed the patch
    // always applies, but it silently doesn't when the cache is missing or a
    // listing fetch in flight overwrites it, and the folder then stays
    // invisible until a reload (founder 08-06, reproduced 4×: POST 200, row in
    // user_folders, absent from the grid). The patch keeps the paint instant;
    // this guarantees it can never be the last word.
    onSettled: () => qc.invalidateQueries({ queryKey: filesKey(userId) }),
  });
}

export function useRenameFolder() {
  const { user } = useAuth();
  const userId = user?.id ?? null;
  const getHeaders = useAuthHeaders();
  const qc = useQueryClient();
  const { toast } = useToast();

  return useMutation<
    void,
    Error,
    { folderId: string; newName: string },
    OptimisticContext
  >({
    mutationFn: async ({ folderId, newName }) => {
      const headers = await getHeaders(true);
      const res = await resilientFetch(
        `${SERVER_URL}/api/folders/${folderId}`,
        {
          method: "PATCH",
          headers,
          body: JSON.stringify({ name: newName }),
        }
      );
      if (!res.ok) throw new Error("Failed to rename folder");
    },
    ...buildOptimisticHandlers<{ folderId: string; newName: string }>(
      qc,
      userId,
      (cur, { folderId, newName }) => ({
        ...cur,
        folders: cur.folders.map((f) =>
          f.id === folderId ? { ...f, name: newName } : f
        ),
      })
    ),
    onError: (err, { newName }, ctx) => {
      if (ctx?.previous) qc.setQueryData(filesKey(userId), ctx.previous);
      toast(`Couldn't rename folder to "${newName}". Reverted.`);
    },
  });
}

export function useDeleteFolder() {
  const { user } = useAuth();
  const userId = user?.id ?? null;
  const getHeaders = useAuthHeaders();
  const qc = useQueryClient();
  const { toast } = useToast();

  return useMutation<void, Error, string, OptimisticContext>({
    mutationFn: async (folderId) => {
      const headers = await getHeaders();
      const res = await resilientFetch(
        `${SERVER_URL}/api/folders/${folderId}`,
        {
          method: "DELETE",
          headers,
        }
      );
      if (!res.ok) throw new Error("Failed to delete folder");
    },
    ...buildOptimisticHandlers<string>(qc, userId, (cur, folderId) => {
      // Cascade: child files → root, child folders → root, then drop
      // the folder. Matches the server behavior in deleteUserFolder().
      return {
        ...cur,
        files: cur.files.map((f) =>
          f.folderId === folderId ? { ...f, folderId: null } : f
        ),
        folders: cur.folders
          .filter((f) => f.id !== folderId)
          .map((f) => (f.parentId === folderId ? { ...f, parentId: null } : f)),
      };
    }),
    onError: (_err, folderId, ctx) => {
      if (ctx?.previous) qc.setQueryData(filesKey(userId), ctx.previous);
      const name =
        ctx?.previous?.folders.find((f) => f.id === folderId)?.name ?? "folder";
      toast(`Couldn't delete folder "${name}". Reverted.`);
    },
  });
}

// ─── Upload ───────────────────────────────────────────────────────────────
//
// Three steps per file: ask the server for a signed URL → PUT the bytes →
// confirm (upload-pipeline.ts). The bytes go up through putWithProgress (XHR),
// because fetch cannot report upload progress and the browser shows none for
// a background request. Each file's row, progress and failure lives in the ONE
// upload store (uploadStore.ts) that the tray reads, whichever component
// started the upload: a mutation per caller hid every upload but its own
// owner's (founder 2026-10-01). No optimistic listing row (there is no
// metadata until the confirm); the listing refetches once the batch settles.

export function useUploadFiles(): (args: {
  files: FileList | File[];
  folderId?: string | null;
}) => void {
  const { user } = useAuth();
  const userId = user?.id ?? null;
  const qc = useQueryClient();

  return ({ files, folderId }) => {
    if (!userId) return; // signed out: nothing to upload as
    void uploadBatch({
      // Snapshot now: a picker's FileList is live and empties when the input
      // resets (pick-files.ts).
      files: Array.from(files),
      folderId: folderId ?? null,
      ownerId: userId,
      // The token and whose it is, read together from the live session for
      // every file: a batch that outlives a sign-out stops instead of going up
      // as whoever signed in next.
      getSession: async () => {
        const {
          data: { session },
        } = await supabase.auth.getSession();
        if (!session) return null;
        return {
          userId: session.user.id,
          headers: {
            Authorization: `Bearer ${session.access_token}`,
            "Content-Type": "application/json",
          },
        };
      },
      onSettled: () => {
        void qc.invalidateQueries({ queryKey: filesKey(userId) });
      },
    });
  };
}
